import { apiFetch } from '../../lib/api';
import { type DocumentWithUser, type DocumentsFilter } from './types';

/**
 * Admin documents API — calls api-node /api/admin/documents/* directly
 * (ported from the web page's fetches against /api/admin/documents).
 */

export interface DocumentsListResponse {
  documents: DocumentWithUser[];
  pagination: { total: number; limit: number; offset: number; hasMore: boolean };
}

/**
 * The document queue. Omit `filter` (or pass `'all'`) to read EVERY status in
 * one call — that is what the Documents screen does.
 *
 * It used to always pass `status=<active tab>`, which made the tab counts a
 * lie: the response only ever contained the tab being looked at, so the other
 * three rendered «0» over a real backlog. The artboard prints a count on all
 * four tabs, and a count is only worth drawing if it is true, so the page reads
 * the full list once and filters in the browser. `limit` is raised to the API's
 * ceiling (200) for the same reason.
 *
 * The parameter stays OPTIONAL rather than being dropped: the dashboard's
 * "documents waiting" tile asks this endpoint for `pending` alone and must keep
 * its narrow query.
 */
export function fetchDocuments(filter?: DocumentsFilter): Promise<DocumentsListResponse> {
  return apiFetch<DocumentsListResponse>('admin/documents', {
    searchParams: {
      status: filter && filter !== 'all' ? filter : undefined,
      limit: '200',
    },
  });
}

export function approveDocument(documentId: string): Promise<{ message: string }> {
  return apiFetch(`admin/documents/${documentId}/approve`, { method: 'POST' });
}

export function rejectDocument(
  documentId: string,
  reason: string,
): Promise<{ message: string }> {
  return apiFetch(`admin/documents/${documentId}/reject`, { method: 'POST', body: { reason } });
}

export function getDocumentViewUrl(
  documentId: string,
): Promise<{ presignedUrl: string; fileName?: string; expiresIn: number }> {
  return apiFetch(`admin/documents/${documentId}/view`);
}
